/**
 * @fileoverview localMinimize -- the dispatch boundary between the BMaps
 * minimization flow (cmds/ComputationCmds.js `energyMinimize`) and the
 * in-browser engine (`LigandMinimizer`).
 *
 * It is the in-browser stand-in for `connector.cmdEnergyMinimize(requestArgs)`:
 * for the same batch of compounds it returns a `ResponseData`-shaped object
 * (see {@link LocalMinimizeResponse}) that `energyMinimize` consumes exactly as
 * it consumes a server response -- `byId(ResponseIds.Compound)` for the
 * minimized poses, `byId(ResponseIds.EnergiesForLigand)` for the energies, and
 * `byId(ResponseIds.Error)` / `hasError()` for failures.
 *
 * This replaces only the interactive MM minimization path; the AEnergy/BFD
 * server path is untouched. A Local-engine failure is surfaced as an
 * `EnergyInfo` error (a per-compound failure message) and an Error packet; it
 * does not auto-fail-over to the server (that failover is gated on
 * `minimizationEngine === 'MM'` in `energyMinimize`).
 *
 * ## Units (the boundary conversion)
 * The engine works in nm / kJ/mol / e / rad; BMaps works in Angstrom /
 * kcal/mol. This module is where the conversion happens:
 *   - starting coords: Angstrom -> nm is done inside `getTypedParameters`.
 *   - minimized coords: engine nm -> Angstrom (x10) here, before write-back.
 *   - energies: engine kJ/mol -> kcal/mol (/4.184) here, before marshalling.
 *
 * ## Receptor set / NoCutoff
 * The engine evaluates the ligand<->receptor interaction with no distance cutoff
 * (mirroring mm-server's `NonbondedForce::NoCutoff`), so the receptor atom set
 * handed to `prepareReceptor` is the interaction set: every retained atom
 * contributes. To agree with mm-server we feed the same residue-pruned set
 * mm-server feeds OpenMM. mm-server prunes by whole residue on the residue-
 * center -> ligand-center distance (`pruneResiduesBeyondCutoff`, mm-server.elp
 * ~1178-1232): a neutral residue is kept within (15 A + minDistToProtein), a
 * charged residue within (50 A + minDistToProtein), where minDistToProtein is
 * the ligand center's distance to the nearest other residue center.
 * `buildReceptorAtoms` reproduces that selection. `pocketRadius` is set to cover
 * the whole selected set with `cutoffBuffer = 0`, so the prepared context keeps
 * everything selected. After minimizing, mm-server adaptively doubles the
 * neutral cutoff (charged stays fixed at 50 A) and rebuilds the receptor if the
 * interaction vdW > 100 kcal/mol (one retry); `localMinimize` mirrors that loop.
 *
 * ## Receptor LJ parameters
 * The v6 forcefield trailer is per-ligand; the server does not ship receptor
 * sigma/epsilon. Receptor LJ params are sourced client-side from the bundled
 * `AmberData` table (amber atom type -> {radius=Rmin/2 A, epsilon kcal/mol}),
 * converted to engine units with the same `sigma = 2*rVDW / 2^(1/6)` convention
 * the server uses. Receptor charges come from `atom.charge` (the v5 atom-info
 * path). A receptor atom missing an amber type or LJ-table entry is dropped from
 * the candidate set with a console warning -- a known v1 limitation.
 *
 * ## Manual dev-server smoke test
 * The full BMaps frontend can't run here, so this is left for manual checking on
 * a dev server: set Preferences -> Minimization Engine -> "In-Browser
 * (experimental)", load a benchmark receptor + ligand (e.g. 4YZU + 4K6), and
 * click Minimize. Confirm the viewer updates to the new pose, the EnergyDisplay
 * shows the new energy, and (DevTools -> Network -> WS) no frame goes to
 * mm-server -- the only server call is a cache-cold `get-forcefield-params` to
 * bfd-server, and re-minimizing the same ligand makes zero forcefield calls.
 *
 * @see proto/src/cmds/ComputationCmds.js (energyMinimize routing)
 * @see proto/src/chemistry/LocalMinimizer/index.js (LigandMinimizer)
 * @see proto/src/chemistry/LocalMinimizer/forcefield/getTypedParameters.js
 */

import AmberData from 'BMapsSrc/chemistry/AmberData';
import { undoUsalignTransform } from 'BMapsSrc/util/transform_utils';
import { getPreferences } from 'BMapsSrc/redux/prefs/access';
import { KJ_TO_KCAL, KCAL_TO_KJ } from 'BMapsSrc/util/units';
import {
    residueGeometricCenter, isChargedResidue, minDistToProtein, pointDistance,
} from 'BMapsSrc/util/atom_distance_utils';
import { ResponseIds } from '../../server/server_common';
import { doWithActiveSolute } from '../../server/activeSolute';
import { LigandMinimizer } from './index.js';
import { getTypedParameters } from './forcefield/getTypedParameters.js';
import { Evaluator } from './forces/evaluator.js';
import { evalBonds, evalAngles, evalTorsions } from './forces/bonded.js';
import { evalLigandNonbonded } from './forces/nonbonded.js';

/**
 * @typedef {import('../../model/compound.js').Compound} Compound
 * @typedef {import('../../model/CaseData.js').default} CaseData
 * @typedef {import('../../DataConnection.js').default} DataConnection
 * @typedef {import('../../model/atoms.js').Atom} Atom
 * @typedef {import('./types.js').ReceptorAtoms} ReceptorAtoms
 */

/** Angstrom -> nm. */
const A_TO_NM = 0.1;
/** nm -> Angstrom. */
const NM_TO_A = 10;
/**
 * sigma = 2 * (Rmin/2) / 2^(1/6). AmberData stores `radius` = Rmin/2 (Angstrom),
 * so sigma_A = radius * SIGMA_PER_VDW_RADIUS, then * A_TO_NM for engine nm.
 * Matches the server's `SigmaPerVdwRadius`.
 */
const SIGMA_PER_VDW_RADIUS = 2 / 2 ** (1 / 6);

/**
 * Receptor residue-pruning cutoffs (Angstrom), replicating mm-server's
 * `pruneResiduesBeyondCutoff` (mm-server.elp ~lines 1178-1232). mm-server prunes
 * by whole residue on the residue-center -> ligand-center distance, keeping a
 * neutral residue within (NEUTRAL_CUTOFF_A + minDistToProtein) and a charged
 * residue within (CHARGED_CUTOFF_A + minDistToProtein) of the ligand center,
 * where minDistToProtein is the distance from the ligand center to the nearest
 * other residue center.
 *
 * NEUTRAL_CUTOFF_A is 15 A: in the bfd-server -> mm-server pipeline, bfd-server
 * forwards its 15 A `defaultResidueCutoff` as mm-server's neutral `residueCutoff`
 * (mm-server's own 25 A default never fires). CHARGED_CUTOFF_A is mm-server's
 * hardcoded 50 A charged-residue cutoff (charges assumed shielded by the protein
 * dielectric). This residue-center prune is the single, always-on selection.
 * @type {number}
 */
const NEUTRAL_CUTOFF_A = 15;
/** @type {number} */
const CHARGED_CUTOFF_A = 50;

/**
 * Adaptive-retry parameters, mirroring mm-server's post-minimization retry
 * (mm-server.elp ~lines 979-992): if the ligand<->receptor interaction vdW
 * exceeds 100 kcal/mol after minimizing, the pruning radius was too small (a
 * residue the ligand drifted into was dropped), so double the neutral cutoff
 * (mm-server.elp ~989 doubles only `residueCutoff`; the charged cutoff stays
 * fixed at 50 A), rebuild the receptor, and re-minimize. mm-server allows one
 * retry (`attempt < 2`).
 * @type {number}
 */
const RETRY_VDW_THRESHOLD_KCAL = 100;
/** @type {number} */
const MAX_MINIMIZE_ATTEMPTS = 2;

/**
 * Engine-native nonbonded cutoff (nm). Mirrors `NO_CUTOFF` in
 * `forces/evaluator.js`: mm-server's NoCutoff convention means every pair
 * interacts; the receptor candidate set is already residue-pruned by
 * `prepareReceptor` so a huge cutoff here is a no-op for vdW/Coulomb sums.
 * @type {number}
 */
const NO_CUTOFF_NM = 1e9;

/**
 * Production minimizer options for the in-browser engine. These override the
 * engine's permissive defaults (minimizer/minimize.js: maxIterations=500,
 * lineSearch='armijo') so the in-browser result reaches OpenMM's minimum; the
 * engine defaults stay loose for cheap unit fixtures.
 *
 * Convergence is OpenMM-compatible: the criterion is the RMS force norm
 * `||grad||_2 / sqrt(nAtoms) <= forceTolerance`, with `forceTolerance` =
 * OpenMM's LocalEnergyMinimizer default of 10 kJ/mol/nm. A line-search stall is
 * reported as success (the structure is minimized to the force precision),
 * matching OpenMM, so the default WASM f32 backend -- whose RMS gradient floors
 * out above any sane tolerance -- still terminates successfully. historySize
 * stays at the engine default (50). maxIterations (5000) is the genuine
 * non-success cap for the rare ligand that keeps progressing without stalling or
 * reaching the tolerance.
 */
export const PROD_LINE_SEARCH = 'wolfe';
export const PROD_FORCE_TOLERANCE = 10; // kJ/mol/nm, RMS force norm (OpenMM default)
export const PROD_MAX_ITERATIONS = 5000;

/**
 * Re-evaluate the minimized pose with per-term decomposition, in kJ/mol.
 *
 * The engine's online evaluator returns a single total energy on the hot path
 * (`Evaluator.evaluate`), so per-term values aren't available from
 * `MinimizationResult`. We recover them here, post-minimize, by calling each
 * force kernel individually against the minimized coords. Costs ~one
 * `Evaluator.evaluate` worth of work (sub-millisecond for a typical ligand).
 *
 * The vdW/Coulomb split of the ligand-internal and ligand-receptor nonbonded
 * sums uses the **zero-charges trick**: rerun the same kernel with all ligand
 * charges set to zero so the Coulomb term vanishes, then `vdW = result(q=0)`
 * and `Coulomb = total - vdW`. No engine API churn; both kernels already
 * accept a per-call charges array.
 *
 * @param {import('./types.js').LigandInput} ligandInput
 * @param {import('./forces/receptor.js').ReceptorContext|null} receptorContext
 *   Prepared receptor, or null for vacuum.
 * @param {Float64Array} minimizedCoords  Length 3*N, nm; the engine's final coords.
 * @returns {{
 *   bondEnergy: number,
 *   angleEnergy: number,
 *   torsionEnergy: number,
 *   ligandVdW: number,
 *   ligandCoulomb: number,
 *   interactionVdW: number,
 *   interactionCoulomb: number,
 *   internalTotal: number,
 *   interactionTotal: number
 * }}  All energies in kJ/mol.
 */
function computeEnergyBreakdown(ligandInput, receptorContext, minimizedCoords) {
    const nAtoms = ligandInput.topology.nAtoms;
    const scratch = new Float64Array(3 * nAtoms);
    const zeroCharges = new Float64Array(ligandInput.parameters.charges.length);

    // Build a fresh Evaluator just to access cached topology + parameter arrays
    // in the same shape the eval kernels consume. The Evaluator's own
    // `evaluate` is not used here -- we want individual term values.
    const ev = new Evaluator(ligandInput, null);

    const bondEnergy = evalBonds(
        minimizedCoords, ev.bonds, ev.bondK, ev.bondR0, scratch,
    );
    scratch.fill(0);
    const angleEnergy = evalAngles(
        minimizedCoords, ev.angles, ev.angleK, ev.angleTheta0, scratch,
    );
    scratch.fill(0);
    const torsionEnergy = evalTorsions(
        minimizedCoords,
        ev.torsions,
        ev.torsionTermsPerTorsion,
        ev.torsionK,
        ev.torsionN,
        ev.torsionPhase,
        scratch,
    );
    scratch.fill(0);

    // Ligand-internal nonbonded: total then vdW-only via the zero-charges trick.
    const ligandNbTotal = evalLigandNonbonded(
        minimizedCoords, ev.charges, ev.sigma, ev.epsilon,
        ev.exclusions, ev.scaledPairs, NO_CUTOFF_NM, scratch,
    );
    scratch.fill(0);
    const ligandVdW = evalLigandNonbonded(
        minimizedCoords, zeroCharges, ev.sigma, ev.epsilon,
        ev.exclusions, ev.scaledPairs, NO_CUTOFF_NM, scratch,
    );
    const ligandCoulomb = ligandNbTotal - ligandVdW;
    scratch.fill(0);

    // Ligand <-> receptor: same trick.
    let interactionVdW = 0;
    let interactionCoulomb = 0;
    if (receptorContext && receptorContext.nCandidates > 0) {
        const interactionTotal = receptorContext.evaluate(
            minimizedCoords, ev.charges, ev.sigma, ev.epsilon, NO_CUTOFF_NM, scratch,
        );
        scratch.fill(0);
        interactionVdW = receptorContext.evaluate(
            minimizedCoords, zeroCharges, ev.sigma, ev.epsilon, NO_CUTOFF_NM, scratch,
        );
        interactionCoulomb = interactionTotal - interactionVdW;
    }

    return {
        bondEnergy,
        angleEnergy,
        torsionEnergy,
        ligandVdW,
        ligandCoulomb,
        interactionVdW,
        interactionCoulomb,
        internalTotal: bondEnergy + angleEnergy + torsionEnergy + ligandNbTotal,
        interactionTotal: interactionVdW + interactionCoulomb,
    };
}

/**
 * Minimal `ResponseData`-compatible result. `energyMinimize` only calls
 * `byId(...)`, `hasError()`, and reads `errors`, so we implement exactly that
 * surface (see proto/src/server_responders.js `ResponseData`).
 *
 * `localUpdatedInPlace` marks that the compounds in the Compound packet are the
 * same objects already living in the workspace, with coordinates mutated in
 * place. `energyMinimize` uses this to skip `addReplaceCompounds` (which would
 * destructively self-replace a compound by its own spec). The server path,
 * which returns brand-new compound objects, does not set this flag.
 */
class LocalMinimizeResponse {
    constructor() {
        /** @type {Object<string|number, any[]>} */
        this.responses = {};
        /** @type {boolean} */
        this.localUpdatedInPlace = true;
    }

    /** @param {string|number} responseId @param {*} data */
    addResponse(responseId, data) {
        if (this.responses[responseId]) this.responses[responseId].push(data);
        else this.responses[responseId] = [data];
    }

    /** @param {string|number|Array} responseIdOrArray @returns {any[]} */
    byId(responseIdOrArray) {
        const out = [];
        for (const [key, packets] of Object.entries(this.responses)) {
            const parsed = Number(key);
            const resId = Number.isNaN(parsed) ? key : parsed;
            if (resId === responseIdOrArray
                || (Array.isArray(responseIdOrArray) && responseIdOrArray.includes(resId))) {
                out.push(...packets);
            }
        }
        return out;
    }

    get errors() { return this.byId(ResponseIds.Error); }

    hasError() { return this.byId(ResponseIds.Error).length > 0; }
}

/**
 * Process-wide (per-tab) shared {@link LigandMinimizer}. Constructing a minimizer
 * spins up a Web Worker and compiles the WASM module inside it; doing that per
 * Minimize click (the old `new LigandMinimizer()` + `dispose()` per batch) re-paid
 * that cold-start every time. A single long-lived instance pays it once and lets
 * the worker's WASM module + receptor cache survive across minimizations.
 *
 * Invariant: callers must not run overlapping minimizations on the shared
 * instance -- `minimize()` keeps per-request state (`receptorContext` read at
 * postMessage time) that is only correct under serialized calls. `localMinimize`
 * awaits each compound sequentially and `energyMinimize` awaits the whole call,
 * so this holds today; preserve it.
 * @type {LigandMinimizer|null}
 */
let _sharedMinimizer = null;

/**
 * Get the shared per-tab minimizer, constructing it (and lazily its worker) on
 * first use. Pre-warm it ahead of the first Minimize via
 * `getSharedMinimizer().warmUp()` (wired in display_mgr.js).
 * @returns {LigandMinimizer}
 */
export function getSharedMinimizer() {
    if (!_sharedMinimizer) _sharedMinimizer = new LigandMinimizer();
    return _sharedMinimizer;
}

/**
 * Tear down the shared minimizer (terminates its worker) and clear the slot so
 * the next `getSharedMinimizer()` builds a fresh one. Called when the user
 * switches the minimization engine away from the in-browser engine. Safe to call
 * when none exists.
 * @returns {void}
 */
export function disposeSharedMinimizer() {
    if (_sharedMinimizer) {
        _sharedMinimizer.dispose();
        _sharedMinimizer = null;
    }
}

/**
 * Run the in-browser minimizer over a batch of compounds and return a
 * server-shaped response.
 *
 * @param {Compound[]} batchCmpds   Compounds to minimize (one batch).
 * @param {object} requestArgs      The same args object built by
 *   `energyMinimize` for `cmdEnergyMinimize` (forcefield, minimizationSteps,
 *   etc.). Only `forcefield` and `minimizationSteps` are consulted here.
 * @param {{ caseData: CaseData, connector: DataConnection }} ctx
 *   Data parents resolved by `energyMinimize` (its `caseData` + `connector`).
 *   Every server command sent from here is scoped by `resSpec`, so each send
 *   is paired with a `set-active-solute` in the same turn via
 *   doWithActiveSolute, or it can resolve against another loaded structure
 *   (BP2-2663).
 * @returns {Promise<LocalMinimizeResponse>}
 */
export async function localMinimize(batchCmpds, requestArgs, ctx) {
    const { caseData, connector } = ctx;
    const response = new LocalMinimizeResponse();

    const atomInfoManager = caseData.getCaseDataCollection()?.atomInfoMaps;
    if (!atomInfoManager) {
        // No way to source forcefield params; fail the whole batch (structured).
        for (const compound of batchCmpds) {
            failCompound(response, compound, 'Local minimization unavailable: no AtomInfoManager for this case.');
        }
        return response;
    }

    // Reuse the shared per-tab minimizer: constructing one spins up a Web Worker
    // + compiles WASM, so a persistent instance amortizes that across the session
    // instead of paying it per batch. The receptor is (re)prepared per compound
    // inside the loop, because the receptor set is the residues within the prune
    // radius of this ligand -- which differs per compound. Calls are serialized
    // (awaited per compound), satisfying the shared-instance "no overlapping
    // minimize" invariant; see getSharedMinimizer.
    const minimizer = getSharedMinimizer();

    // Production engine options (see PROD_* constants above). requestArgs.minimizationSteps,
    // when the caller provides it (e.g. a UI "steps" control), overrides maxIterations;
    // everything else stays at the production tuning. minimizationSteps defaults to null
    // (ComputationCmds.energyMinimize), so the fallback is PROD_MAX_ITERATIONS.
    const options = {
        lineSearch: PROD_LINE_SEARCH,
        forceTolerance: PROD_FORCE_TOLERANCE,
        maxIterations: requestArgs?.minimizationSteps || PROD_MAX_ITERATIONS,
    };

    for (const compound of batchCmpds) {
        try {
            // 1) Typed params + starting coords (nm). Throws on server-unreachable,
            //    FF mismatch, or no v6 data -> caught below, surfaced as failure.
            const ligandInput = await getTypedParameters(
                compound, atomInfoManager, connector,
                { forcefield: requestArgs?.forcefield },
            );

            // 2-3) Prepare the receptor + minimize, with mm-server's adaptive
            //    cutoff-doubling retry (mm-server.elp ~979-992): if the post-
            //    minimization interaction vdW exceeds the clash threshold, the
            //    prune radius was too small, so double the neutral cutoff (charged
            //    stays at 50 A, matching mm-server.elp ~989), rebuild the
            //    receptor, and re-minimize (one retry). The receptor is
            //    ligand-specific, so it is (re)prepared per attempt; the
            //    minimizer caches an identical prepared set internally. Each
            //    attempt minimizes from the original starting coords (a fresh
            //    clone) so a clashed first attempt doesn't bias the retry.
            let result;
            let breakdown;
            // Count of receptor atoms dropped for lacking a force-field (LJ) type
            // on the attempt whose result we keep. mm-server drops nothing, so a
            // non-zero count means the receptor we scored is incomplete and the
            // energies may differ from the server -- surfaced to the user via the
            // energy packet (see energyPacket / applyEnergiesToCompound).
            let droppedReceptorAtoms = 0;
            const startCoords = Float64Array.from(ligandInput.coords);
            // mm-server's adaptive cutoff-doubling retry (always on): if the first
            // attempt's interaction vdW exceeds the clash threshold, double the
            // neutral cutoff (charged stays at 50 A), rebuild the receptor, and
            // re-minimize once.
            const maxAttempts = MAX_MINIMIZE_ATTEMPTS;
            for (let attempt = 0; attempt < maxAttempts; attempt++) {
                // Scales the neutral residue cutoff only (selectReceptorResidues),
                // matching mm-server.elp ~989 which doubles only `residueCutoff`.
                const cutoffScale = 2 ** attempt;
                const receptor = buildReceptorAtoms(compound, caseData, cutoffScale);
                droppedReceptorAtoms = receptor ? receptor.dropped : 0;
                if (receptor && receptor.coords.length > 0) {
                    const { center, radiusNm } = ligandPocket(compound, caseData, cutoffScale);
                    // pocketRadius covers the whole selected set (we already
                    // pruned by residue around the ligand); cutoffBuffer = 0.
                    minimizer.prepareReceptor(receptor, center, radiusNm, 0);
                } else {
                    // No receptor (vacuum): clear any context a prior compound
                    // in the batch left on the reused minimizer.
                    minimizer.clearReceptor();
                }

                // Minimize (engine units throughout) from the original pose.
                ligandInput.coords = Float64Array.from(startCoords);
                result = await minimizer.minimize(ligandInput, options);

                // 3b) Per-term energy decomposition at the minimized pose.
                //     Recovers the bond/angle/torsion/vdW/coulomb breakdown the
                //     UI's EnergyDisplay path expects (Stress Delta = sum of
                //     internals, ligand-receptor vdW + coulomb split for the
                //     H-bond decomposition in `processElectrostatic`). Recomputed
                //     here rather than carried out of `minimize()` because the
                //     L-BFGS hot path only sums into a single total.
                breakdown = computeEnergyBreakdown(
                    ligandInput, minimizer.receptorContext, result.coords,
                );

                const lastAttempt = attempt === maxAttempts - 1;
                const hasReceptor = minimizer.receptorContext
                    && minimizer.receptorContext.nCandidates > 0;
                if (lastAttempt || !hasReceptor) break;
                // breakdown.interactionVdW is kJ/mol; mm-server's threshold is kcal/mol.
                const interactionVdWKcal = breakdown.interactionVdW * KJ_TO_KCAL;
                if (interactionVdWKcal <= RETRY_VDW_THRESHOLD_KCAL) break;
                console.warn(
                    `localMinimize: interaction vdW ${interactionVdWKcal.toFixed(2)} kcal/mol `
                    + `exceeds ${RETRY_VDW_THRESHOLD_KCAL} for ${compound.resSpec}; retrying with `
                    + 'a doubled neutral receptor cutoff (mm-server adaptive prune).',
                );
            }

            // Guard against a non-finite minimized result. A NaN/Inf in the
            // starting coords yields gradMaxAbs 0, so minimize.js's in-loop NaN
            // guard never fires (the loop body doesn't run) and the engine returns
            // a bogus "converged" result still carrying the NaN. Without this check
            // the NaN would be written back onto the atoms and pushed to the server.
            // Fail the compound instead (-> the per-compound catch -> failCompound),
            // so nothing is written back or pushed.
            if (!Number.isFinite(result.energy)
                || result.coords.some((c) => !Number.isFinite(c))) {
                throw new Error(
                    'the minimizer produced a non-finite result (check the input coordinates)',
                );
            }

            // 3c) Isolated/relaxed reference for the strain (server "get-energies"
            //     convention): minimize the ligand ALONE (no receptor) from the
            //     current (bound) pose, then use its ligand-internal energy as the
            //     reference. Strain = bound internal - relaxed internal, per term
            //     (see strainInternalSummary). Sequential/awaited -> preserves the
            //     shared-minimizer "no overlapping minimize" invariant. The next
            //     compound rebuilds ligandInput + re-preps the receptor, so nulling
            //     receptorContext here doesn't leak. Order matters: the bound
            //     `breakdown` (which needs the bound receptorContext) is already
            //     computed inside the loop; this runs AFTER it, then nulls context.
            // start from the bound minimized pose
            ligandInput.coords = Float64Array.from(result.coords);
            minimizer.clearReceptor(); // vacuum: ignore the protein
            const relaxedResult = await minimizer.minimize(ligandInput, options);
            const relaxedBreakdown = computeEnergyBreakdown(
                ligandInput, null, relaxedResult.coords,
            );
            // Intentionally NO writeBackCoords(relaxedResult): the displayed pose
            // must stay the bound/minimized-in-pocket pose.
            // No absolute-internal fallback: if the isolated relax throws it
            // propagates to the per-compound catch -> failCompound, mirroring
            // mm-server, which errors the energy request when its unbound-reference
            // minimize fails (mm-server.elp MMMinimize: `if (!ok) return false` ->
            // finishWithError). Silently reporting the absolute internal would
            // surface a wrong "strain".

            // 4) Write minimized coords back into the compound (nm -> Angstrom,
            //    undoing any alignment transform), then marshal the response.
            //    The engine atom index space is the atomNames order (the same
            //    order getTypedParameters used to fill the starting coords).
            const { atomNames } = atomInfoManager.getLigandAtomInfo(compound.resname) || {};
            writeBackCoords(compound, atomNames, result);

            // 4b) Push the minimized coords to the server. The minimization
            //     happened entirely in-browser, so without this the server still
            //     holds the pre-minimization pose -- a later "Get energies" or
            //     the GetSolvation call energyMinimize fires right after this
            //     returns would score the stale pose. Send raw structure-frame
            //     Angstroms (the display/usalign transform is not applied: the
            //     server stores coords in the raw frame). Keep this single
            //     expression as the coord source so switching to the display
            //     frame (getX/getY/getZ) is a one-line change. A sync failure
            //     must not fail the minimization (the local result is already
            //     valid) -- log and continue; the server energies just won't
            //     reflect the new pose.
            try {
                const atoms = compound.getAtoms().map((a) => ({
                    name: a.atom, x: a.rawX, y: a.rawY, z: a.rawZ,
                }));
                await doWithActiveSolute(compound, () => connector.cmdUpdateLigandCoords({
                    spec: compound.resSpec, atoms,
                }));
            } catch (syncErr) {
                console.warn(
                    `localMinimize: failed to sync minimized coords for ${compound.resSpec} `
                    + `to the server; later scoring may use the stale pose: ${syncErr?.message || syncErr}`,
                );
            }

            response.addResponse(ResponseIds.Compound, compound);
            response.addResponse(
                ResponseIds.EnergiesForLigand,
                energyPacket(compound, result, breakdown, relaxedBreakdown, droppedReceptorAtoms),
            );
        } catch (err) {
            // Local does not auto-fail-over: surface the structured message on
            // this compound and keep going.
            failCompound(response, compound, err?.message || String(err));
        }
    }

    // Do not dispose -- the shared minimizer (its worker + WASM module + receptor
    // cache) is intentionally long-lived. Teardown happens via
    // disposeSharedMinimizer() when the user leaves the in-browser engine.
    return response;
}

/**
 * Mark a single compound's minimization as failed and record the error so the
 * existing display/`gatherEnergyErrors` pathway picks it up, exactly like a
 * server error packet would.
 *
 * @param {LocalMinimizeResponse} response
 * @param {Compound} compound
 * @param {string} message
 * @returns {void}
 */
function failCompound(response, compound, message) {
    const full = `In-browser minimization failed for ${compound.resSpec}: ${message}`;
    compound.energyInfo.updateMinimizationRequest(false, full);
    response.addResponse(ResponseIds.Error, full);
}

/**
 * Internal-strain summary (bound − isolated-relaxed), per term, in kcal/mol.
 *
 * Mirrors the server's "get-energies" convention (bfd-server `sendMMEnergies`):
 * the reported ligand-internal stress is the BOUND internal minus the
 * ISOLATED-RELAXED internal, per force group. The slot layout matches the
 * server wire format `internalEnergies[0] = [bonded, 0, 0, 0, vdW, coulomb]`:
 * the bonded strain (bond + angle + torsion) is collapsed into slot 0 and slots
 * 1–3 are 0. `applyEnergiesToCompound` sums this array into `Types.stress`, so
 * collapsing the bonded terms into one slot is invisible to the displayed total.
 *
 * `relaxed` is REQUIRED: there is no absolute-internal fallback. If the isolated
 * relax fails, the caller fails the compound (mirroring mm-server, which errors
 * the energy request when its unbound-reference minimize fails) rather than
 * reporting an absolute internal mislabeled as strain.
 *
 * @param {{
 *   bondEnergy: number, angleEnergy: number, torsionEnergy: number,
 *   ligandVdW: number, ligandCoulomb: number
 * }} bound    Bound (in-pocket) ligand-internal breakdown, kJ/mol.
 * @param {{
 *   bondEnergy: number, angleEnergy: number, torsionEnergy: number,
 *   ligandVdW: number, ligandCoulomb: number
 * }} relaxed  Isolated-relaxed ligand-internal breakdown, kJ/mol (required).
 * @returns {number[]} `[bonded, 0, 0, 0, vdW, coulomb]` in kcal/mol.
 */
export function strainInternalSummary(bound, relaxed) {
    // No absolute-internal fallback (see above): `relaxed` is required.
    const boundBonded = bound.bondEnergy + bound.angleEnergy + bound.torsionEnergy;
    const relaxedBonded = relaxed.bondEnergy + relaxed.angleEnergy + relaxed.torsionEnergy;
    return [
        (boundBonded - relaxedBonded) * KJ_TO_KCAL,
        0, 0, 0,
        (bound.ligandVdW - relaxed.ligandVdW) * KJ_TO_KCAL,
        (bound.ligandCoulomb - relaxed.ligandCoulomb) * KJ_TO_KCAL,
    ];
}

/**
 * Build the engine-shaped energy packet from a {@link MinimizationResult} plus
 * the per-term breakdown computed by `computeEnergyBreakdown`.
 *
 * Shape mirrors decoder.js `decodeEnergiesForLigand`:
 *   internalEnergies   = [summary(6), details(6 lists)]
 *   interactionEnergies = [summary(2), details(2 lists)]
 *
 * ## Internal summary slot layout
 * The internal summary is the per-term STRAIN (bound − isolated-relaxed),
 * matching the server's get-energies convention; see `strainInternalSummary`.
 * Its slot layout mirrors bfd-server `sendMMEnergies`: the bonded strain is
 * collapsed into slot 0 (`[bonded, 0, 0, 0, vdW, coulomb]`). The
 * `processElectrostatic` UI path (energyinfo.js) only consumes the SUM of
 * internals (for Stress Delta), so the slot collapse is invisible to the
 * displayed numbers. `relaxedBreakdown` is always present here -- a failed
 * isolated relax fails the compound (no fallback; see `strainInternalSummary`).
 *
 * ## Interaction summary slot layout
 * `[vdw, coulomb]` from the ligand <-> static receptor sum. The H-bond /
 * "other electrostatics" split that the UI displays is computed downstream by
 * `processElectrostatic(compound, coulomb, soluteAtoms)` and only needs the
 * total interaction-coulomb number.
 *
 * Details arrays remain empty -- the engine doesn't compute the per-atom
 * detail rows the server emits; only the summary numbers are required for the
 * EnergyDisplay path.
 *
 * @param {Compound} compound
 * @param {import('./types.js').MinimizationResult} result
 * @param {{
 *   bondEnergy: number, angleEnergy: number, torsionEnergy: number,
 *   ligandVdW: number, ligandCoulomb: number,
 *   interactionVdW: number, interactionCoulomb: number
 * }} breakdown   Bound (in-pocket) breakdown, kJ/mol; converted to kcal/mol here.
 * @param {{
 *   bondEnergy: number, angleEnergy: number, torsionEnergy: number,
 *   ligandVdW: number, ligandCoulomb: number
 * }} relaxedBreakdown  Isolated-relaxed breakdown, kJ/mol (required; a failed
 *   relax fails the compound before this is called -- no fallback).
 * @param {number} [droppedReceptorAtoms=0]  Count of receptor atoms omitted from
 *   the interaction because they had no force-field (LJ) type. mm-server drops
 *   none, so a non-zero count means the receptor was incomplete; surfaced to the
 *   user as a warning in applyEnergiesToCompound.
 * @returns {{
 *   compoundSpec: string, internalEnergies: any[], interactionEnergies: any[], raw: object
 * }}
 */
function energyPacket(compound, result, breakdown, relaxedBreakdown, droppedReceptorAtoms = 0) {
    // Internal summary: per-term STRAIN (bound − isolated-relaxed) in kcal/mol,
    // slot layout [bonded, 0, 0, 0, vdW, coulomb] (server sendMMEnergies).
    const internalSummary = strainInternalSummary(breakdown, relaxedBreakdown);
    // Details lists, one per summary term (empty -> no per-atom detail rows).
    const internalDetails = [[], [], [], [], [], []];
    // Interaction summary: [vdw, coulomb] kcal/mol; details: [vdwList, coulombList].
    const interactionSummary = [
        breakdown.interactionVdW * KJ_TO_KCAL,
        breakdown.interactionCoulomb * KJ_TO_KCAL,
    ];
    const interactionDetails = [[], []];

    return {
        compoundSpec: compound.resSpec,
        internalEnergies: [internalSummary, internalDetails],
        interactionEnergies: [interactionSummary, interactionDetails],
        raw: {
            engine: 'in-browser',
            energyKJ: result.energy,
            totalEnergyKcal: result.energy * KJ_TO_KCAL,
            // `success` (not `converged`) gates the non-convergence warning: a
            // line-search stall at the resolvable minimum is a success (OpenMM-
            // style), so only a maxIterations cutoff or non-finite state warns.
            success: result.success,
            terminationReason: result.terminationReason,
            converged: result.converged,
            // Count of receptor atoms omitted from the interaction for lacking a
            // force-field (LJ) type. mm-server omits none, so a non-zero count
            // means the receptor we scored was incomplete and energies may differ
            // from the server -- surfaced as a user warning by applyEnergiesToCompound.
            droppedReceptorAtoms,
            iterations: result.iterations,
            finalGradMaxAbs: result.finalGradMaxAbs,
            finalGradRms: result.finalGradRms,
            wallTimeMs: result.wallTimeMs,
            // Diagnostics for the strain-based stress (fix #1): the per-term strain
            // (kcal/mol) reported as internalSummary, and the isolated-relaxed
            // ligand-internal reference energy (kJ/mol).
            strainKcal: internalSummary,
            relaxedInternalKJ: relaxedBreakdown.internalTotal,
        },
    };
}

/**
 * Write the minimized coordinates back onto the compound's atoms, converting
 * engine nm -> Angstrom and undoing any alignment transform so the stored
 * `rawX/rawY/rawZ` (which `getX/Y/Z()` re-transform on read) are consistent
 * with the frame the engine minimized in. `getTypedParameters` read the
 * starting coords via `atom.getX/Y/Z()` (the transformed/display frame), so the
 * engine output is in that same frame; we invert the transform before storing.
 *
 * Frame symmetry: the read path (`Atom.getPosition` in atoms.js) only applies
 * the usalign transform when the `UseAlignment` preference is on -- i.e.
 * `getX/Y/Z()` return the raw coords (transform not applied) when `UseAlignment`
 * is off, even if the atom carries a non-null transform. We must therefore undo
 * the transform here under the same condition: only when `UseAlignment` is on.
 * Undoing unconditionally would, when a transform exists but `UseAlignment` is
 * off, un-apply a transform the engine never saw on read, corrupting both the
 * stored (displayed) pose and the server coord-push. Gating on
 * `getPreferences().UseAlignment` mirrors `getPosition`'s gate so read and write
 * can't diverge.
 *
 * @param {Compound} compound
 * @param {string[]|undefined} atomNames  The atomNames order = the engine atom
 *   index space (getTypedParameters placed atom `a` at slot 3*atomNames.indexOf
 *   (a.atom)). Falls back to the compound's atom order if absent.
 * @param {import('./types.js').MinimizationResult} result  coords (nm), atomNames order.
 * @returns {void}
 */
export function writeBackCoords(compound, atomNames, result) {
    const { coords } = result;
    // Build name -> engine-index, reproducing getTypedParameters' mapping.
    /** @type {Map<string, number>} */
    const nameToIdx = new Map();
    if (atomNames && atomNames.length > 0) {
        atomNames.forEach((name, i) => nameToIdx.set(name, i));
    } else {
        compound.getAtoms().forEach((atom, i) => nameToIdx.set(atom.atom, i));
    }

    // Mirror the read path's gate (Atom.getPosition): the transform was applied
    // on read only when UseAlignment was on, so we undo it only when it is on.
    // This keeps read and write in the same frame (see the function doc).
    const { UseAlignment } = getPreferences();

    for (const atom of compound.getAtoms()) {
        const idx = nameToIdx.get(atom.atom);
        if (idx == null) continue; // not part of the minimized set
        const nx = coords[3 * idx] * NM_TO_A;
        const ny = coords[3 * idx + 1] * NM_TO_A;
        const nz = coords[3 * idx + 2] * NM_TO_A;
        // The engine worked in the frame getTypedParameters read via getX/Y/Z():
        // the display frame when UseAlignment is on (transform applied), the raw
        // frame when it is off (transform not applied). Undo the transform only
        // in the former case so write-back round-trips the read symmetrically.
        const transform = UseAlignment ? atom.getAtomTransform?.() : null;
        const [rx, ry, rz] = transform
            ? undoUsalignTransform([nx, ny, nz], transform)
            : [nx, ny, nz];
        atom.rawX = rx;
        atom.rawY = ry;
        atom.rawZ = rz;
    }
}

/**
 * Geometric center (mean atom position, Angstrom) of the ligand compound,
 * mirroring mm-server's `residue->GetGeometricCenter` for the compound being
 * minimized.
 *
 * @param {Compound} compound
 * @returns {[number, number, number]} Center in Angstrom.
 */
function ligandGeometricCenter(compound) {
    const atoms = compound.getAtoms();
    let sx = 0; let sy = 0; let sz = 0;
    for (const a of atoms) { sx += a.getX(); sy += a.getY(); sz += a.getZ(); }
    const n = atoms.length || 1;
    return [sx / n, sy / n, sz / n];
}

/**
 * Group the solute atoms into residue-like units for residue-granular pruning,
 * mirroring mm-server's iteration over `solute.residues`. Protein/polymer atoms
 * carry `atom.residue` (a Residue/DecodedResidue); cofactor/ion atoms fall back
 * to their `AtomGroup` (the whole cofactor/ion IS the residue unit). Atoms with
 * neither are bucketed individually so they're never silently dropped.
 *
 * @param {Atom[]} soluteAtoms
 * @returns {Array<{ getAtoms?: function(): Atom[], atoms?: Atom[], formalCharge?: number }>}
 *   Distinct residue-like units, in first-seen order.
 */
function groupSoluteResidues(soluteAtoms) {
    const units = new Map();
    let fallbackKey = 0;
    for (const atom of soluteAtoms) {
        const unit = atom.residue || atom.getAtomGroup?.();
        if (unit) {
            if (!units.has(unit)) units.set(unit, unit);
        } else {
            // No residue/group association: treat this atom as its own unit.
            const key = `__atom_${fallbackKey++}`;
            units.set(key, { atoms: [atom] });
        }
    }
    return [...units.values()];
}

/**
 * Select the receptor residue-like units to retain for `compound`, replicating
 * mm-server's `pruneResiduesBeyondCutoff` (mm-server.elp ~1178-1232):
 *
 *   - Work on residue-geometric-center -> ligand-geometric-center distances.
 *   - `minDist` = distance to the nearest OTHER residue center.
 *   - Keep a NEUTRAL residue within (NEUTRAL_CUTOFF_A*scale + minDist).
 *   - Keep a CHARGED residue within (CHARGED_CUTOFF_A*scale + minDist).
 *
 * `cutoffScale` scales only the neutral cutoff on an adaptive retry, matching
 * mm-server (mm-server.elp ~989), which doubles its neutral `residueCutoff` and
 * leaves the charged cutoff fixed at 50 A. Scaling charged too would keep extra
 * far charged residues (a 50-100 A shell) mm-server prunes.
 *
 * @param {Compound} compound
 * @param {Atom[]} soluteAtoms
 * @param {number} cutoffScale  Multiplier on the neutral cutoff only (1 normally,
 *   2 on retry); the charged cutoff stays fixed at 50 A.
 * @returns {{
 *   center: [number, number, number],
 *   keptAtoms: Atom[],
 *   minDist: number,
 *   neutralCutoffA: number,
 *   chargedCutoffA: number
 * }}  center in Angstrom; keptAtoms are the atoms of all selected residues.
 */
export function selectReceptorResidues(compound, soluteAtoms, cutoffScale = 1) {
    const center = ligandGeometricCenter(compound);
    const ligandResidue = compound; // the compound IS the ligand's residue unit
    const residues = groupSoluteResidues(soluteAtoms);

    const minDist = minDistToProtein(center, residues, ligandResidue);
    // Adaptive-retry alignment with mm-server (mm-server.elp ~989): the retry
    // scales only the neutral cutoff (`emargs.residueCutoff = savedCutoff*2`) and
    // leaves the charged cutoff fixed at 50 A. Scaling both would keep extra far
    // charged residues (a 50-100 A shell) mm-server prunes, so charged stays
    // unscaled here.
    const neutralCutoffA = NEUTRAL_CUTOFF_A * cutoffScale;
    const chargedCutoffA = CHARGED_CUTOFF_A;
    const neutralKeep = neutralCutoffA + minDist;
    const chargedKeep = chargedCutoffA + minDist;

    const keptAtoms = [];
    for (const res of residues) {
        if (res === ligandResidue) continue;
        const dist = pointDistance(center, residueGeometricCenter(res));
        const keepDist = isChargedResidue(res) ? chargedKeep : neutralKeep;
        if (dist <= keepDist) {
            const atoms = typeof res.getAtoms === 'function' ? res.getAtoms() : (res.atoms || []);
            keptAtoms.push(...atoms);
        }
    }
    return {
        center, keptAtoms, minDist, neutralCutoffA, chargedCutoffA,
    };
}

/**
 * Compute the ligand pocket center + radius (nm) used as `prepareReceptor`
 * input. Center is the ligand geometric center; radius is generous enough to
 * retain every atom of the residue set `buildReceptorAtoms` selects (the same
 * 15/50 A + minDistToProtein selection), so `ReceptorContext.prepareReceptor`'s
 * center-distance prune never drops a residue we kept.
 *
 * @param {Compound} compound
 * @param {CaseData} caseData
 * @param {number} [cutoffScale=1]  Multiplier on the neutral cutoff only (matches
 *   the adaptive-retry scale passed to `buildReceptorAtoms`; charged stays at 50 A).
 * @returns {{ center: [number, number, number], radiusNm: number }}  center in nm.
 */
function ligandPocket(compound, caseData, cutoffScale = 1) {
    const soluteAtoms = caseData.getSoluteAtoms?.() || [];
    const { center, keptAtoms } = selectReceptorResidues(compound, soluteAtoms, cutoffScale);
    // Radius = the farthest kept receptor atom from the ligand center, so the
    // center-based prune in ReceptorContext.prepareReceptor retains the whole
    // selected set. Falls back to the (unscaled) charged cutoff when nothing is
    // kept -- the retry scales only the neutral cutoff, so the charged shell that
    // bounds the kept set does not grow with cutoffScale (see selectReceptorResidues).
    let maxD = CHARGED_CUTOFF_A;
    for (const a of keptAtoms) {
        const d = pointDistance(center, [a.getX(), a.getY(), a.getZ()]);
        if (d > maxD) maxD = d;
    }
    return {
        center: [center[0] * A_TO_NM, center[1] * A_TO_NM, center[2] * A_TO_NM],
        radiusNm: maxD * A_TO_NM,
    };
}

/**
 * Build the receptor {@link ReceptorAtoms} set for the engine, replicating
 * mm-server's residue-granular prune (`pruneResiduesBeyondCutoff`): keep whole
 * residues whose geometric center is within (15 A + minDistToProtein) when
 * neutral, or (50 A + minDistToProtein) when charged, of the ligand geometric
 * center (W2 contract). Coordinates are taken in the display frame (matching the
 * ligand coords from getTypedParameters); charges from `atom.charge`;
 * sigma/epsilon from the bundled AmberData LJ table keyed by amber atom type.
 *
 * Atoms whose amber type is missing/unknown in the AmberData LJ table are
 * dropped (a known v1 limitation) and surfaced via `console.warn` and the
 * returned `dropped` count.
 *
 * @param {Compound} compound   The ligand being minimized.
 * @param {CaseData} caseData
 * @param {number} [cutoffScale=1]  Multiplier on the neutral residue cutoff only;
 *   the adaptive-retry loop passes 2 to double it (mm-server.elp ~979-992 doubles
 *   only the neutral `residueCutoff`; the charged cutoff stays fixed at 50 A).
 * @returns {(ReceptorAtoms & { dropped: number })|null}  null if there is no
 *   solute to build from. `dropped` is the count of LJ-less atoms removed.
 */
export function buildReceptorAtoms(compound, caseData, cutoffScale = 1) {
    const soluteAtoms = caseData.getSoluteAtoms?.() || [];
    if (soluteAtoms.length === 0) return null;

    // mm-server residue-center selection: neutral within 15 A, charged within
    // 50 A (+ minDistToProtein), whole residues. See selectReceptorResidues.
    const { keptAtoms } = selectReceptorResidues(compound, soluteAtoms, cutoffScale);

    const coords = [];
    const charges = [];
    const sigma = [];
    const epsilon = [];
    let dropped = 0;
    for (const atom of keptAtoms) {
        const lj = atom.amber ? AmberData.getRadiusData(atom.amber) : null;
        if (!lj || lj.radius == null || lj.epsilon == null) {
            // No LJ params for this receptor atom (missing/unknown amber type):
            // drop it from the candidate set. Known v1 limitation.
            dropped++;
            continue;
        }
        coords.push(atom.getX() * A_TO_NM, atom.getY() * A_TO_NM, atom.getZ() * A_TO_NM);
        charges.push(typeof atom.charge === 'number' ? atom.charge : 0);
        sigma.push(lj.radius * SIGMA_PER_VDW_RADIUS * A_TO_NM);
        epsilon.push(lj.epsilon * KCAL_TO_KJ); // kcal/mol -> kJ/mol
    }
    if (dropped > 0) {
        console.warn(
            `localMinimize: dropped ${dropped}/${keptAtoms.length} receptor atoms `
            + 'with no AmberData LJ parameters (missing/unknown amber type). '
            + 'Receptor interaction energy may diverge from mm-server for these.',
        );
    }

    return {
        coords: Float64Array.from(coords),
        charges: Float64Array.from(charges),
        sigma: Float64Array.from(sigma),
        epsilon: Float64Array.from(epsilon),
        dropped,
    };
}
